import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, of, startWith, takeUntil, timer } from 'rxjs';
import { environment } from '../../../environment/environment';
import { UrlConstant } from '../../intake/constants/url.constant';
import { ArchiveDocument } from '../../common/models/archive-document.model';
import { DocumentType } from '../../common/models/document-type.model';
import { ProgressIndicatorService } from '../../common/services/progress-indicator.service';
import { SharedService } from '../../common/services/shared-service.service';
import { interval, switchMap, takeWhile } from 'rxjs';
import { DownloadDocumentModel } from '../../common/models/common-models';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  ongoingSubscriptions: Set<number> = new Set();

  constructor(
    private readonly httpClient: HttpClient,
    private readonly progressIndicator: ProgressIndicatorService,
    private readonly sharedService: SharedService
  ) {}
  baseURL: string = environment.apiUrl;
  //baseURL: string = 'https://dapi.relay.deloitte.com/api/'; //dev
  // baseURL: string = 'https://qapi.relay.deloitte.com/api/'; //qa

  getPresignedUrlDetails(data: any): Observable<any> {
    const url = this.baseURL + UrlConstant.GET_PRESIGNED_DETAILS_PATH;
    return this.httpClient.post(url, data, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('idToken'),
      }),
    });
  }

  saveDocumentMetaData(data: any): Observable<any> {
    const url = this.baseURL + UrlConstant.GET_SAVE_DOCUMENT_META__DATA_PATH;
    let finalData = JSON.stringify(data);
    return this.httpClient.post(url, finalData, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('idToken'),
      }),
    });
  }

  updateDocumentMetaData(data: any): Observable<any> {
    const url = this.baseURL + UrlConstant.GET_UPDATE_DOCUMENT_META__DATA_PATH;
    let finalData = JSON.stringify(data);
    return this.httpClient.post(url, finalData, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('idToken'),
      }),
    });
  }
  updateDocumentName(data: any): Observable<any> {
    const url = this.baseURL + UrlConstant.UPDATE_DOCUMENT_NAME;
    let finalData = JSON.stringify(data);
    return this.httpClient.post(url, finalData, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('idToken'),
      }),
    });
  }
  documentUploadByPresignedURL(data: any, fileSource: any): Observable<any> {
    const url = data.url;
    return this.httpClient.put(url, fileSource, {
      headers: new HttpHeaders({
        'Content-Type': 'binary/octet-stream',
        'x-amz-server-side-encryption': 'aws:kms',
        'x-amz-server-side-encryption-aws-kms-key-id': data.kmsArn,
      }),
    });
  }

  getDownloadUrl(downloadRequest: any): Observable<any> {
    var data = JSON.stringify(downloadRequest);
    const url = this.baseURL + UrlConstant.GET_DOWNLOAD_PATH;
    return this.httpClient.post(url, data, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('idToken'),
      }),
    });
  }

  getArchivedDocDownloadUrl(docId: number): Observable<any> {
    const url = this.baseURL + UrlConstant.GET_ARCHIVE_DOWNLOAD_PATH + docId;
    return this.httpClient.get(url, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('idToken'),
      }),
    });
  }

  deleteDocument(documentKey: any, fileFolderPath: any): Observable<any> {
    const url =
      this.baseURL +
      UrlConstant.GET_DELETE_DOCUMENT_PATH +
      documentKey +
      '&fileFolderPath=' +
      fileFolderPath;
    return this.httpClient.delete(url, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('idToken'),
      }),
    });
  }

  exportClientDataPDF(
    clientNumber: string,
    isNCA: boolean,
    isCC: boolean,
    isMars: boolean,
    isMarsAccess: boolean,
    clientMilestoneId: number
  ): void {
    const url = this.baseURL + UrlConstant.GET_EXPORT_CLIENT_DATA_PDF_PATH;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/pdf',
    });
    this.progressIndicator.show();
    this.httpClient
      .post<Blob>(
        url,
        { clientNumber, isNCA, isCC, isMars, isMarsAccess, clientMilestoneId },
        { headers, observe: 'response', responseType: 'blob' as 'json' }
      )
      .subscribe(
        (response: any) => {
          const blob = new Blob([response.body], { type: 'application/pdf' });
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          let fileName = 'ClientData.pdf'; // Default file name

          if (contentDisposition) {
            const matches = /filename="([^"]*)"/.exec(contentDisposition);
            if (matches != null && matches[1]) {
              fileName = matches[1];
            }
          }

          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          this.progressIndicator.hide();
        },
        (error) => {
          console.error('Download error:', error);
          this.progressIndicator.hide();
        }
      );
  }

  exportToPDF(req: any, module: string = 'intake'): Observable<any> {
    const endpoint = () => {
      switch (module) {
        case 'intake':
          return UrlConstant.GET_PDF_DOWNLOAD_PATH;
        case 'extcomm':
          return UrlConstant.EXT_COMM_GET_PDF_DOWNLOAD_PATH;
        case 'global':
          return UrlConstant.GLOBAL_ENG_GET_PDF_DOWNLOAD_PATH;
        default:
          return '';
      }
    };
    const url = this.baseURL + endpoint();

    if (module === 'intake') {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        accept: 'text/plain',
      });
      return this.httpClient.post<string>(url, req, {
        headers,
        responseType: 'text' as 'json',
      });
    } else {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        accept: 'application/pdf',
      });
      return this.httpClient.post<Blob>(url, req, {
        headers,
        observe: 'response',
        responseType: 'blob' as 'json',
      });
    }
  }

  downloadPDFPolling(requestBody: any, module: string = 'intake'): void {
    const endpoint = () => {
      switch (module) {
        case 'intake':
          return UrlConstant.INTAKE_DOWNLOAD_SUBMISSION_PDF;
        case 'extcomm':
          return UrlConstant.INTAKE_DOWNLOAD_SUBMISSION_PDF; // TODO: Confirm the endpoint with BE
        case 'global':
          return UrlConstant.INTAKE_DOWNLOAD_SUBMISSION_PDF; // TODO: Confirm the endpoint with BE
        default:
          return '';
      }
    };

    // TODO: If same endpoint for all module then directly use that.
    const downloadUrl: string = this.baseURL + endpoint();

    this.ongoingSubscriptions.add(requestBody.submissionid);
    const pollingInterval = 5000;
    const maxPollingTime = 2 * 60 * 1000; // 2 minutes
    const polling$ = interval(pollingInterval).pipe(
      startWith(0),
      switchMap(() =>
        this.httpClient
          .post<DownloadDocumentModel>(downloadUrl, requestBody)
          .pipe(
            // catchError is used if we want to continue polling even after error is encountered
            catchError((err) => {
              console.error('Error while downloading the PDF:', err);
              return of(null);
            })
          )
      ),
      takeWhile((response) => !response?.documentName, true),
      takeUntil(timer(maxPollingTime)) // Stop polling after 2 minutes
    );

    let timeout: boolean = true;
    const subscription = polling$.subscribe({
      next: (response: any) => {
        if (response?.documentDownloadUrl && response?.documentName) {
          this.downloadDocument(response, requestBody.submissionid);
          timeout = false;
          subscription.unsubscribe();
          this.removeSubscription(requestBody.submissionid); // Remove from ongoingSubscriptions
        }
      },
      // Will get executed only when catchError is not present
      error: (err: HttpErrorResponse) => {
        console.error('Error while downloading the PDF:', err);
        // subscription.unsubscribe();
      },
      complete: () => {
        this.removeSubscription(requestBody.submissionid); // Remove from ongoingSubscriptions
        if (timeout) {
          this.sharedService.showToastNotification(
            `PDF download failed for submission ${requestBody.submissionid}. Please try again.`
          );
          subscription.unsubscribe();
        }
      },
    });
  }

  private removeSubscription(submissionId: number): void {
    this.ongoingSubscriptions.delete(submissionId);
  }

  downloadDocument(
    response: DownloadDocumentModel,
    submissionId: number
  ): void {
    let fileName = response?.documentName ?? 'Submission.pdf'; // Default file name
    const a = document.createElement('a');
    a.id = `${submissionId}`;
    a.href = response?.documentDownloadUrl;
    a.target = '_blank'; // Open in new tab
    a.rel = 'noopener noreferrer'; // Prevents switching to new tab
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    this.sharedService.showToastNotification(
      `The PDF for submission ${submissionId} has been successfully downloaded.`
    );
    document.body.removeChild(a);
  }

  getDocumentArchiveId(
    archiveNumber: string,
    submissionId: number,
    submissionTypeId: number,
    submissionSubTypeId: string | number | null,
    EntityTypeId: number
  ): Observable<ArchiveDocument> {
    const url = this.baseURL + UrlConstant.GET_ARCHIVE_SUBMISSION_ID_PATH;
    const body = {
      ArchiveNumber: archiveNumber,
      SubmissionId: submissionId,
      SubmissionTypeId: submissionTypeId,
      SubmissionSubTypeId: submissionSubTypeId,
      EntityTypeId: EntityTypeId,
    };
    return this.httpClient.post<ArchiveDocument>(url, body);
  }

  getDocumentTypes(): Observable<DocumentType[]> {
    const url = this.baseURL + UrlConstant.GET_DOCUMENT_TYPES_PATH;
    return this.httpClient.get<DocumentType[]>(url, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('idToken'),
      }),
    });
  }

  generateArchiveSubmissionPDF(
    submissionId: number,
    opportunityNumber: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      `${this.baseURL}${UrlConstant.GENERATE_ARCHIVE_SUBMISSION_PDF}${submissionId}&opportunityNumber=${opportunityNumber}`
    );
  }

  //getDocumentUploadData(attachmentID:string): Observable<DocumentTypeData[]> {
  //  const url = this.baseURL + 'Document/GetDocumentMetaData?attachmentID=' + attachmentID;
  //  return this.myhttp.get<DocumentTypeData[]>(url, {
  //    headers: new HttpHeaders({
  //      'Content-Type': 'application/json',
  //      'Authorization': "Bearer " + localStorage.getItem("idToken")
  //    })
  //  })
  //}

  getUploadUrlForDCRMValidation(documentName: any): Observable<any> {
    const url =
      this.baseURL +
      UrlConstant.GET_PRESIGNED_DETAIS_FOR_DCRM_VALIDATION +
      documentName;
    return this.httpClient.get(url, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('idToken'),
      }),
    });
  }

  uploadFileToS3UsingPresignedUrl(data: any, fileSource: any): Observable<any> {
    const url = data.url;
    return this.httpClient.put(url, fileSource, {
      headers: new HttpHeaders({
        'Content-Type': 'binary/octet-stream',
        'x-amz-server-side-encryption': 'aws:kms',
        'x-amz-server-side-encryption-aws-kms-key-id': data.kmsArn,
      }),
    });
  }

  uploadDocument(documentName: any, documentKey: any, filePath: any) {
    const url = this.baseURL + UrlConstant.GET_UPLOAD_DOCUMENT_PATH;
    const jsonPayload = {
      documentName: documentName.toString(),
      documentKey: documentKey.toString(),
      fileFolderPath: filePath.toString(),
    };
    return this.httpClient.post(url, jsonPayload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + localStorage.getItem('idToken'),
      }),
    });
  }

  extCommDownloadPdf(req: any): Observable<any> {
    const url = this.baseURL + UrlConstant.EXT_COMM_GET_PDF_DOWNLOAD_PATH;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/pdf',
    });
    return this.httpClient.post<Blob>(url, req, {
      headers,
      observe: 'response',
      responseType: 'blob' as 'json',
    });
  }

  getMigratedDocumnetArchiveUrl(clientMilestoneId: number): Observable<any> {
    const url =
      this.baseURL + UrlConstant.GET_MIGRATED_COMPLETED_DOC + clientMilestoneId;
    return this.httpClient.get(url, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('idToken'),
      }),
    });
  }
}
